import { Helmet } from 'react-helmet-async';

const SEO_CONFIG = {
  siteName: 'MindForge',
  defaultTitle: 'MindForge | Mental Performance Training',
  defaultDescription: 'Unlock your full potential with AI-powered mental training. Guided meditations and personalized coaching for athletes, golfers, and business leaders.',
  siteUrl: 'https://mindboost-37.emergent.app',
  defaultImage: '/og-image.png'
};

export default function SEO({ 
  title, 
  description, 
  path = '',
  image,
  type = 'website'
}) {
  const pageTitle = title 
    ? `${title} | ${SEO_CONFIG.siteName}` 
    : SEO_CONFIG.defaultTitle;
  const pageDescription = description || SEO_CONFIG.defaultDescription;
  const pageUrl = `${SEO_CONFIG.siteUrl}${path}`;
  const pageImage = image || `${SEO_CONFIG.siteUrl}${SEO_CONFIG.defaultImage}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
    </Helmet>
  );
}

// Pre-configured SEO for each page
export const PageSEO = {
  Landing: () => (
    <SEO 
      title="Mental Performance Training for Athletes & Leaders"
      description="Unlock your full potential with AI-powered mental training. Guided meditations, visualization exercises, and personalized coaching."
      path="/"
    />
  ),
  Dashboard: () => (
    <SEO 
      title="Dashboard"
      description="Track your mental training progress, view daily affirmations, and access quick actions."
      path="/dashboard"
    />
  ),
  Coach: () => (
    <SEO 
      title="AI Mental Coach"
      description="Get personalized mental coaching powered by AI. Overcome anxiety, build confidence, and achieve peak performance."
      path="/coach"
    />
  ),
  Meditations: () => (
    <SEO 
      title="Guided Meditations"
      description="Access guided meditation and visualization exercises designed for athletes, golfers, and business professionals."
      path="/meditations"
    />
  ),
  Goals: () => (
    <SEO 
      title="Goal Tracking"
      description="Set, track, and achieve your performance goals with structured progress monitoring."
      path="/goals"
    />
  ),
  Journal: () => (
    <SEO 
      title="Reflection Journal"
      description="Document your mental training journey, process experiences, and build mental resilience."
      path="/journal"
    />
  ),
  Affirmations: () => (
    <SEO 
      title="Daily Affirmations"
      description="Start each day with powerful, category-specific affirmations for mental programming."
      path="/affirmations"
    />
  ),
  Achievements: () => (
    <SEO 
      title="Achievements"
      description="Track your mental training milestones and unlock badges as you progress."
      path="/achievements"
    />
  ),
  Analytics: () => (
    <SEO 
      title="Performance Analytics"
      description="View detailed analytics of your mental training progress and identify patterns for growth."
      path="/analytics"
    />
  ),
  Subscription: () => (
    <SEO 
      title="Premium Subscription"
      description="Unlock unlimited AI coaching, all meditations, and full analytics with MindForge Premium."
      path="/subscription"
    />
  ),
};
